// src/lib/opportunity/formatDocumentSlotTypes.ts

import { FORMAT_CALC_PRODUCT_TYPE_WILDCARD } from "./formatCalcLineTypes.js";
import type { OpportunityFormatKey } from "../../types/opportunity.js";

export const FORMAT_DOCUMENT_SLOT_KEY_MAX_LEN = 64;
export const FORMAT_DOCUMENT_SLOT_LABEL_MAX_LEN = 120;

export type FormatDocumentSlot = {
  id: string;
  organization_id: string;
  format_key: OpportunityFormatKey;
  product_type: string;
  slot_key: string;
  label: string;
  helper_text: string | null;
  sort_order: number;
  required_before_signed: boolean;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type FormatDocumentSlotDef = Pick<
  FormatDocumentSlot,
  "slot_key" | "label" | "helper_text" | "sort_order" | "required_before_signed"
>;

const GENERIC_DEFAULT_SLOTS: FormatDocumentSlotDef[] = [
  {
    slot_key: "document",
    label: "Document",
    helper_text: "Supporting documents for this opportunity.",
    sort_order: 0,
    required_before_signed: false,
  },
  {
    slot_key: "signed_contract",
    label: "Signed contract",
    helper_text:
      "Customer-signed contract PDF after external signature. Generated packages live under Generated documents.",
    sort_order: 1,
    required_before_signed: false,
  },
];

const RED_IRON_DEFAULT_SLOTS: FormatDocumentSlotDef[] = [
  {
    slot_key: "nucor",
    label: "Nucor",
    helper_text: "Manufacturer quotes, drawings, and spec packages.",
    sort_order: 0,
    required_before_signed: false,
  },
  {
    slot_key: "erector",
    label: "Erector",
    helper_text: "Erector bids, scope-of-work agreements, and crew schedules.",
    sort_order: 1,
    required_before_signed: false,
  },
  {
    slot_key: "financing",
    label: "Financing",
    helper_text: "Loan applications, financing approvals, and term sheets.",
    sort_order: 2,
    required_before_signed: false,
  },
  {
    slot_key: "customer",
    label: "Customer",
    helper_text: "Customer-provided drawings, permits, prior estimates.",
    sort_order: 3,
    required_before_signed: false,
  },
  {
    slot_key: "signed_contract",
    label: "Signed contract",
    helper_text:
      "Customer-signed contract PDF after external signature. Generated packages live under Generated documents.",
    sort_order: 4,
    required_before_signed: false,
  },
];

export function defaultDocumentSlots(formatKey: OpportunityFormatKey): FormatDocumentSlotDef[] {
  if (formatKey === "red_iron") return [...RED_IRON_DEFAULT_SLOTS];
  return [...GENERIC_DEFAULT_SLOTS];
}

export function selectFormatDocumentSlots(args: {
  slots: FormatDocumentSlot[];
  productType: string;
  formatKey: OpportunityFormatKey;
}): FormatDocumentSlotDef[] {
  const specific = args.slots.filter((s) => s.product_type === args.productType);
  const wildcard = args.slots.filter((s) => s.product_type === FORMAT_CALC_PRODUCT_TYPE_WILDCARD);
  const fromDb = specific.length > 0 ? specific : wildcard;
  if (fromDb.length === 0) return defaultDocumentSlots(args.formatKey);
  return [...fromDb]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((s) => ({
      slot_key: s.slot_key,
      label: s.label,
      helper_text: s.helper_text,
      sort_order: s.sort_order,
      required_before_signed: s.required_before_signed === true,
    }));
}

export function slugifyDocumentSlotKey(label: string): string {
  const cleaned = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, FORMAT_DOCUMENT_SLOT_KEY_MAX_LEN);
  return cleaned || "document";
}

export function isPersistedDocumentSlotId(id: string): boolean {
  return !id.startsWith("__default_");
}

export function mergeDocumentSlotsForEditor(
  rows: FormatDocumentSlot[],
  formatKey: OpportunityFormatKey,
  productType: string,
): FormatDocumentSlot[] {
  if (rows.length > 0) {
    return [...rows].sort((a, b) => a.sort_order - b.sort_order);
  }
  return defaultDocumentSlots(formatKey).map((def, idx) => ({
    id: `__default_slot_${def.slot_key}__`,
    organization_id: "",
    format_key: formatKey,
    product_type: productType,
    ...def,
    sort_order: def.sort_order ?? idx,
    required_before_signed: def.required_before_signed ?? false,
    updated_by_user_id: null,
    created_at: "",
    updated_at: "",
  }));
}
